import React, { useState, useEffect, useMemo } from 'react';
import config from "../common/Config";
import { toast } from 'sonner';
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Link as MuiLink,
  useMediaQuery,
} from "@mui/material";
import { Iconify } from "src/components/iconify";
import Swal from 'sweetalert2';
import axios from 'axios';
import "../common/commonStyles.css";
import { encryptPayload, resolveEncryptedResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const columns = [
  { id: "date", label: "Order Date", numeric: false, width: 90 },
  { id: "reference_no", label: "Order Id", numeric: false, width: 80 },
  { id: "customer", label: "Customers (A/C No)", numeric: false, width: 200 },
  { id: "postal_code", label: "Post Code", numeric: false, width: 90 },
  { id: "route_number", label: "Route", numeric: false, width: 70 },
  { id: "grand_total", label: "Order Amt", numeric: true, width: 90 },
  { id: "prev_due", label: "Previous Dues", numeric: true, width: 100 },
  { id: "name", label: "Order By", numeric: false, width: 80 },
  { id: "action", label: "Action", numeric: false, width: 70 },
];

const HoldList = () => {
  const [dates, setDates] = useState([null, null]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectData, setRejectData] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
    }
  }, [accessToken]);

  const handleDateChange = (newDates) => {
    setDates(newDates);
    if (newDates[0] && newDates[1]) {
      setStartDate(format(newDates[0], "yyyy-MM-dd"));
      setEndDate(format(newDates[1], "yyyy-MM-dd"));
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      fetchData();
    }
  };

  const handleClearFilter = () => {
    setDates([null, null]);
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setPage(0);
    fetchData();
  };

  const fetchData = () => {
    if (!accessToken ) {
      return;
    }
    setLoading(true);
    (async () => {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = { authorization: `Bearer ${token}` };
      let body;
      if (useEncrypt) {
        const plain = {
          searchByFromdate: startDate,
          searchByTodate: endDate,
          draw: String(page + 1),
          start: String(page * rowsPerPage),
          length: String(rowsPerPage),
          role: "",
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          setLoading(false);
          toast.error("Failed to load hold list.");
          setData([]);
          setTotalRows(0);
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        body = fd;
      } else {
        const formData = new FormData();
        formData.append("searchByFromdate", startDate);
        formData.append("searchByTodate", endDate);
        formData.append("draw", String(page + 1));
        formData.append("start", String(page * rowsPerPage));
        formData.append("length", String(rowsPerPage));
        formData.append("role", "");
        body = formData;
      }
      fetch(`${config.API_URL}/sales/holdsaleslist`, {
        method: "POST",
        headers,
        body,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch hold list data");
          }
          return response.json();
        })
        .then((json) => {
          const decrypted = useEncrypt ? resolveEncryptedResponse(json) : null;
          const resolved =
            useEncrypt && decrypted && typeof decrypted === "object" ? decrypted : json;
          const rows = Array.isArray(resolved.data) ? resolved.data : [];
          const filteredData = searchTerm
            ? rows.filter((row) =>
                (row.reference_no && row.reference_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (row.customer && row.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (row.postal_code && row.postal_code.toLowerCase().includes(searchTerm.toLowerCase()))
              )
            : rows;
          setData(filteredData);
          setTotalRows(filteredData.length);
        })
        .catch(() => {
          toast.error("Failed to load hold list.");
          setData([]);
          setTotalRows(0);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, startDate, endDate]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionMenuOpen = (event, row) => {
    setActionAnchorEl(event.currentTarget);
    setActionRow(row);
    };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  const handleNewStatus = (id) => {
    
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to transfer this order to new sales?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append('status', 'New');
        formData.append('id', id);
        formData.append('reason_note', ''); 
        formData.append('new-sales', 'No');
        axios
          .post(
            `${config.API_URL}/ws-update-status`,
            formData,
            {
              headers: {
                'authorization': `Bearer ${token}`, 
              },
            }
          )
          .then((response) => {
            toast.success('Order has been transferred!');
            fetchData();
          })
          .catch((error) => {
            toast.error('Something went wrong!');
          });
      }
    });
    handleActionMenuClose();
  };

  const handleTransferToManifest = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to transfer this order to create manifest?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
    const formData = new FormData();
        formData.append('status', 'Invoiced');
        formData.append('id', id);
        formData.append('reason_note', '');
        formData.append('new-sales', 'No');
        axios
          .post(
            `${config.API_URL}/ws-update-status`,
            formData,
            {
        headers: {
          'authorization': `Bearer ${token}`,
        },
            }
          )
          .then((response) => {
            toast.success('Order has been transferred to create manifest!');
            fetchData();
          })
          .catch((error) => {
            toast.error('Something went wrong!');
          });
      }
    });
    handleActionMenuClose();
  };

  const handleRejectClick = (row) => {
    setRejectData(row);
    setRejectComment("");
    setShowRejectModal(true);
    handleActionMenuClose();
  };

  const handleRejectSubmit = () => {
    if (!rejectComment.trim()) {
      toast.error("Please enter reason for reject.");
      return;
    }
    const formData = new FormData();
    formData.append("status", "Rejected");
    formData.append("reason_note", rejectComment);
    formData.append("id", rejectData.ids);
    formData.append("new-sales", "No");
    axios
      .post(`${config.API_URL}/ws-update-status`, formData, {
        headers: { authorization: `Bearer ${token}` },
      })
      .then(() => {
        setShowRejectModal(false);
        setRejectComment("");
        toast.success("Order rejected successfully");
        fetchData();
      })
      .catch(() => {
        toast.error("Error rejecting order");
      });
  };

  const handleViewEdit = (id) => {
    const url = `${config.BASE_URL}/sales/edit/${id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleActionMenuClose();
  };

  const renderCellContent = (columnId, row) => {
    if (columnId === "reference_no") {
      const url = `${config.BASE_URL}/sales/edit/${row.ids}`;
      return (
        <MuiLink
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "underline !important",
            textDecorationColor: "currentColor",
            textUnderlineOffset: "2px",
            "&:hover": {
              textDecoration: "underline !important",
              textDecorationColor: "currentColor"
            }
          }}
        >
          {row.reference_no}
        </MuiLink>
      );
    }
    if (columnId === "action") {
      return (
        <IconButton size="small" onClick={(event) => handleActionMenuOpen(event, row)}>
          <Iconify icon="mdi:dots-vertical" />
        </IconButton>
      );
    }
    const value = row[columnId];
    if (columns.find((column) => column.id === columnId)?.numeric) {
      return value;
    }
    return value ?? "";
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) =>
      (row.reference_no && row.reference_no.toLowerCase().includes(searchLower)) ||
      (row.customer && row.customer.toLowerCase().includes(searchLower)) ||
      (row.postal_code && row.postal_code.toLowerCase().includes(searchLower))
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} alignItems="center" justifyContent="space-between">
              <Grid2 size={{ xs: 12, md: 4}}>
                <Box>
                  <Typography className="page-title">
                    Hold List
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  size="small"
                  placeholder="Search orders..."
                  variant="outlined"
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, md: 4 }}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Box sx={{ width: { xs: '100%', md: '350px'}}}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    onChange={handleDateChange}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  className="button-variant-outlined"
                  startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
                  onClick={handleClearFilter}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  Clear
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer sx={{ width: "100%", overflowX: "auto", position: "relative", "&::-webkit-scrollbar": { height: "8px" }, "&::-webkit-scrollbar-track": { backgroundColor: "#f5f7fa" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(145, 158, 171, 0.32)", borderRadius: "4px" } }}>
            <Table
              size="small"
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                      sx={{
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={`${column.id}-${index}`}
                          align={column.numeric ? "right" : "left"}
                          className="table-cell"
                          sx={{
          
                            width: column.width ? `${column.width}px` : "auto",
                          }}
                        >
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                  : paginatedData.length > 0
                    ? paginatedData.map((row, rowIndex) => (
                      <TableRow
                        key={row.ids || row.reference_no || rowIndex}
                        hover
                        
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(145, 158, 171, 0.04)"
                          }
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.numeric ? "right" : "left"}
                            className="table-cell"
                            sx={{
                              width: column.width ? `${column.width}px` : "auto"
                            }}
                          >
                            {renderCellContent(column.id, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                    : (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ py: 4 }}>
                          <Typography align="center">
                            No records found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredData.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleActionMenuClose}>
        <MenuItem
          onClick={() => {
            if (actionRow) {
              handleViewEdit(actionRow.ids);
            }
          }}
        >
          View/Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionRow) {
              handleNewStatus(actionRow.ids);
            }
          }}
        >
          Transfer to New Sales
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionRow) {
              handleTransferToManifest(actionRow.ids);
            }
          }}
        >
          Transfer to Create Manifest
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionRow) {
              handleRejectClick(actionRow);
            }
          }}
        >
          Reject
        </MenuItem>
      </Menu>

      <Dialog
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectComment("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", color: "#212b36", pb: 1 }}>
          Reject Order
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            py: 1,
            overflow: "hidden",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
          }}
        >
          {rejectData && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
                Order Details
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>Order Id</TableCell>
                    <TableCell>{rejectData.reference_no}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Customer (A/C)</TableCell>
                    <TableCell>{rejectData.customer}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Order Amount</TableCell>
                    <TableCell>{rejectData.grand_total}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Previous Dues</TableCell>
                    <TableCell>{rejectData.prev_due || "N/A"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <TextField
                label="Reason Note"
                value={rejectComment}
                onChange={(event) => setRejectComment(event.target.value)}
                multiline
                minRows={3}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f7fa",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#fff"
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff"
                    }
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    backgroundColor: "#fff",
                    px: 0.5,
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    }
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={() => {
              setShowRejectModal(false);
              setRejectComment("");
            }}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.875rem" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmit}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.875rem", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HoldList;
